import {
    AlertCircle,
    Brain,
    Calendar,
    CheckCircle,
    Clock,
    FileText,
    Phone,
    Shield,
    Users
  } from 'lucide-react';
  import { useEffect, useState } from 'react';
  import { useNavigate } from 'react-router-dom';
  import { useApi } from '../../hooks/useApi';
  import { StatCard } from './components';
  
  const StatusIndicator = ({ status }) => {
    const styles = {
      scheduled: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      'in-progress': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
    };
  
    return (
      <span className={`px-3 py-1.5 text-xs rounded-full font-semibold ${styles[status] || 'bg-gray-100 dark:bg-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  export const StaffDashboard = () => {
    const { get } = useApi();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
      upcomingAppointments: 0,
      completedSessions: 0,
      availableCounselors: 0,
      nextAppointment: null,
      recentSessions: [],
      urgentCareInfo: null,
      notifications: [],
      sessionProgress: {
        goalsSet: 0,
        goalsAchieved: 0,
        sessionsAttended: 0,
        totalSessions: 0
      }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
  
    useEffect(() => {
      const fetchStats = async () => {
        try {
          setLoading(true);
          const response = await get('/dashboard/stats/staff');
          // Ensure we have default values for all properties
          setStats(prev => ({
            ...prev,
            ...response.data.data,
            sessionProgress: {
              ...prev.sessionProgress,
              ...(response.data.data.sessionProgress || {})
            }
          }));
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
  
      fetchStats();
      // Set up periodic refresh
      const interval = setInterval(fetchStats, 300000); // 5 minutes
      return () => clearInterval(interval);
    }, [get]);
  
    const handleBookAppointment = () => {
      navigate('/staff/appointments/new');
    };
  
    const handleViewSessions = () => {
      navigate('/staff/sessions');
    };
  
    const handleViewAppointmentDetails = (appointmentId) => {
      navigate(`/staff/appointments/${appointmentId}`);
    };
  
    // Calculate goals percentage safely
    const calculateGoalsPercentage = () => {
      const goalsSet = stats.sessionProgress?.goalsSet || 0;
      const goalsAchieved = stats.sessionProgress?.goalsAchieved || 0;
      if (goalsSet === 0) return '0';
      return Math.round((goalsAchieved / goalsSet) * 100).toString();
    };
  
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-usiu-blue-600"></div>
        </div>
      );
    }
  
    return (
      <div className="p-6 bg-gray-50 dark:bg-slate-900 min-h-screen">
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600 p-4 rounded-r-xl">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-500" />
              <p className="ml-3 text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}
  
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 bg-clip-text text-transparent">Welcome Back</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">Track your counseling journey and upcoming sessions</p>
        </div>
  
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Upcoming Appointments"
            value={stats.upcomingAppointments || 0}
            icon={Calendar}
            description="Scheduled sessions"
          />
          <StatCard
            title="Sessions Completed"
            value={stats.completedSessions || 0}
            icon={CheckCircle}
            description="Total sessions attended"
          />
          <StatCard
            title="Available Counselors"
            value={stats.availableCounselors || 0}
            icon={Users}
            description="Ready to help"
          />
          <StatCard
            title="Goals Progress"
            value={`${calculateGoalsPercentage()}%`}
            icon={Brain}
            description={`${stats.sessionProgress?.goalsAchieved || 0}/${stats.sessionProgress?.goalsSet || 0} goals achieved`}
          />
        </div>
  
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Next Appointment */}
            {stats.nextAppointment && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <h2 className="text-lg font-semibold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 bg-clip-text text-transparent">Next Appointment</h2>
                  <StatusIndicator status={stats.nextAppointment.status} />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white text-lg">
                        {stats.nextAppointment.counselorName}
                      </p>
                      <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mt-2">
                        <Calendar className="h-4 w-4 mr-2" />
                        {new Date(stats.nextAppointment.date).toLocaleDateString()}
                        <Clock className="h-4 w-4 mx-2" />
                        {stats.nextAppointment.timeSlot}
                      </div>
                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {stats.nextAppointment.serviceType}
                      </div>
                      <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {stats.nextAppointment.sessionType}
                      </div>
                    </div>
                    <button
                      onClick={() => handleViewAppointmentDetails(stats.nextAppointment._id)}
                      className="px-4 py-2 text-sm font-medium text-usiu-blue-600 dark:text-usiu-blue-400 hover:bg-usiu-blue-50 dark:hover:bg-usiu-blue-900/20 rounded-lg transition"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            )}
  
            {/* Recent Sessions */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 bg-clip-text text-transparent">Recent Sessions</h2>
              </div>
              <div className="p-6">
                {stats.recentSessions?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recentSessions.map(session => (
                      <div key={session._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl hover:shadow-md transition">
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">{session.counselorName}</p>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            {new Date(session.date).toLocaleDateString()}
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/staff/sessions/${session._id}`)}
                          className="text-sm font-medium text-usiu-blue-600 dark:text-usiu-blue-400 hover:text-usiu-blue-700 dark:hover:text-usiu-blue-300"
                        >
                          View Notes
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">No recent sessions</p>
                )}
              </div>
            </div>
          </div>
  
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 bg-clip-text text-transparent">Quick Actions</h2>
              </div>
              <div className="p-6 space-y-3">
                <button
                  onClick={handleBookAppointment}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all"
                >
                  <Calendar className="h-5 w-5 mr-2" />
                  Book New Appointment
                </button>
                <button
                  onClick={handleViewSessions}
                  className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  View Session History
                </button>
              </div>
            </div>
  
            {/* Urgent Care Info */}
            {stats.urgentCareInfo && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border-2 border-red-200 dark:border-red-800">
                <div className="px-6 py-4 border-b border-red-200 dark:border-red-800">
                  <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">Urgent Care</h2>
                </div>
                <div className="p-6">
                  <div className="flex items-center text-red-600 dark:text-red-400 mb-4">
                    <Shield className="h-5 w-5 mr-2" />
                    <span className="font-semibold">24/7 Support Available</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    {stats.urgentCareInfo.message}
                  </p>
                  <div className="space-y-2 text-sm">
                    <p className="font-semibold text-gray-900 dark:text-white">Emergency Contacts:</p>
                    {stats.urgentCareInfo.contacts?.map((contact, index) => (
                      <p key={index} className="flex items-center text-gray-600 dark:text-gray-300">
                        <Phone className="h-4 w-4 mr-2" />
                        {contact}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  export default StaffDashboard;